import axios from 'axios';
// Order capabilities are deliberately separate from account login credentials.
const pendingKey = (sessionId) => `kyis_checkout_pending:${sessionId}`;
const orderKey = (orderId) => `kyis_checkout_order:${orderId}`;
const read = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
};
export const readPendingCheckout = (sessionId) => read(pendingKey(sessionId));
export const readCheckoutOrder = (orderId) => orderId ? read(orderKey(orderId)) : null;
export const savePendingCheckout = (sessionId, attempt) => {
  // Fail before creating an order if the browser cannot preserve retry state.
  localStorage.setItem(pendingKey(sessionId), JSON.stringify(attempt));
  if (attempt.order?.id) localStorage.setItem(orderKey(attempt.order.id), JSON.stringify(attempt));
};
export const forgetPendingCheckout = (sessionId, orderId) => {
  const pending = readPendingCheckout(sessionId);
  if (!orderId || pending?.order?.id === orderId) localStorage.removeItem(pendingKey(sessionId));
};
export const checkoutError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => typeof item === 'string' ? item : item.msg).filter(Boolean).join('. ') || fallback;
  return fallback;
};
export const newCheckoutAttemptId = () => window.crypto.randomUUID ? window.crypto.randomUUID() :
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c => (Number(c) ^ window.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c) / 4).toString(16));

// Only errors known to occur before persistence release the immutable attempt.
export const isDefiniteCheckoutRejection = (error) => {
  const { status, data } = error?.response || {};
  if (status === 422) return true;
  if (status !== 400 || typeof data?.detail !== 'string') return false;
  return ['An email address is required for checkout', 'Order must have at least one item',
    'A full shipping address is required for shipped or delivered orders',
    'Selected product option is unavailable'].includes(data.detail) || /^Product .+ not found$/.test(data.detail);
};
const reconciliationRuns = new Map();
const samePurchasedLine = (a, b) => a.id === b.id && a.product_id === b.product_id &&
  (a.variant_id || null) === (b.variant_id || null) && a.quantity === b.quantity &&
  (a.customization_text || null) === (b.customization_text || null) &&
  JSON.stringify(a.customization_options || null) === JSON.stringify(b.customization_options || null);
// Delete unchanged line IDs, never subtract quantities or clear the entire cart.
// Deletion is idempotent even if a response is lost; re-added lines have new IDs.
export const reconcilePaidCheckout = (saved) => {
  if (!saved?.order?.id || !saved.sessionId || !saved.cart?.items) return Promise.resolve();
  const key = `kyis_checkout_reconciled:${saved.order.id}`;
  if (localStorage.getItem(key)) return Promise.resolve();
  if (reconciliationRuns.has(key)) return reconciliationRuns.get(key);
  const run = (async () => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/cart`;
    const query = `session_id=${encodeURIComponent(saved.sessionId)}`;
    for (const purchased of saved.cart.items) {
      // Refresh before every deletion so changes made during payment are retained.
      const { data } = await axios.get(`${base}?${query}`);
      if (data.items?.some(current => samePurchasedLine(current, purchased))) {
        await axios.delete(`${base}/items/${encodeURIComponent(purchased.id)}?${query}`);
      }
    }
    localStorage.setItem(key, '1');
  })().finally(() => reconciliationRuns.delete(key));
  reconciliationRuns.set(key, run);
  return run;
};
